import React, { useEffect } from 'react'

import { FmdGoodOutlined, Inventory2Outlined, LocalShippingOutlined, VerifiedOutlined } from '@mui/icons-material'
import { Avatar, Box, Chip, Divider, Grid, Skeleton, Step, StepLabel, Stepper, Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import Accordion from '../../../../shared/components/Accordion/Accordion.component'
import EmptyState from '../../../../shared/components/EmptyState/EmptyState.component'
import TripDetailsCard from '../Users/components/UserDetails/components/OrderDetails/components/TripDetailsCard/TripDetailsCard.component'

import { getFulfillmentOrderDetailsForAdmin } from '../../../../redux/states/admin/orders/getFulfillmentOrderDetailsForAdmin/getFulfillmentOrderDetailsForAdmin.slice'
import {
  AdminFulfillmentOrderDetails,
  AdminFulfillmentOrderTrackingDetails,
  AdminFulfillmentOrderTrackingStep,
  GetFulfillmentOrderDetailsForAdminRequestPayload
} from '../../../../redux/states/admin/orders/getFulfillmentOrderDetailsForAdmin/getFulfillmentOrderDetailsForAdmin.types'
import { getFulfillmentOrderTripDetailsForAdmin } from '../../../../redux/states/admin/orders/getFulfillmentOrderTripDetailsForAdmin/getFulfillmentOrderTripDetailsForAdmin.slice'
import { GetFulfillmentOrderTripDetailsForAdminRequestPayload } from '../../../../redux/states/admin/orders/getFulfillmentOrderTripDetailsForAdmin/getFulfillmentOrderTripDetailsForAdmin.types'
import { RootState } from '../../../../redux/store/store.types'
import { GRAMS_PER_KILOGRAM } from '../../../../shared/dictionaries/unitConversion.dictionary'
import date, { dateTimeFormats } from '../../../../shared/functions/Date/date.functions'
import { stripPrefixFromId } from '../../../../shared/functions/ID/id.functions'
import { getStatusColor } from '../../../../shared/functions/Status/status.functions'
import { formatMoney } from '../../../../shared/functions/String/string.functions'
import { UserRole } from '../../../../shared/functions/UserRole/userRoleManagement.types'
import { getUserRole } from '../../../../shared/functions/UserRole/userRoleManagment.functions'
import { useAppDispatch } from '../../../../shared/hooks/redux.hooks'
import styles from './FulfillmentOrderDetails.styles'

interface FulfillmentOrderDetailsProps {
  orderId: string
}

const AVATAR_COLORS = ['#2E86AB', '#A23B72', '#F18F01', '#5C4033', '#1B5E20', '#4A148C']

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)

  return parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : name.substring(0, 2).toUpperCase()
}

function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)

  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

const computeActiveStepIndex = (
  trackingSteps: AdminFulfillmentOrderTrackingStep[],
  trackingDetails: AdminFulfillmentOrderTrackingDetails
): number => {
  const visibleSteps = trackingSteps.filter((step) => step.visible)

  let lastCompletedIndex = -1

  for (let i = 0; i < visibleSteps.length; i++) {
    const stepKey = visibleSteps[i].key as keyof AdminFulfillmentOrderTrackingDetails
    const stepDetail = trackingDetails[stepKey]

    if (stepDetail?.status) {
      lastCompletedIndex = i
    }
  }

  return lastCompletedIndex + 1
}

const formatTotalWeightFromGrams = (totalGrams: number): string => {
  if (totalGrams >= GRAMS_PER_KILOGRAM) {
    const kilograms = totalGrams / GRAMS_PER_KILOGRAM
    const rounded = Math.round(kilograms * 10) / 10
    const kilogramsLabel = rounded % 1 === 0 ? `${Math.round(rounded)}` : `${rounded}`

    return `${kilogramsLabel} kg`
  }

  return `${totalGrams} g`
}

const buildOrderPhysicalDetailsSubtitle = (order: AdminFulfillmentOrderDetails): string => {
  const weightGramsRounded = order.orderWeightGrams != null ? Math.round(Number(order.orderWeightGrams)) : null

  const weightPortion = weightGramsRounded != null ? formatTotalWeightFromGrams(weightGramsRounded) : null

  const hasOrderDimensions = order.orderLengthCm != null && order.orderWidthCm != null && order.orderHeightCm != null

  const dimensionsPortion = hasOrderDimensions
    ? `${order.orderLengthCm}×${order.orderWidthCm}×${order.orderHeightCm} cm`
    : null

  if (weightPortion && dimensionsPortion) {
    return `${weightPortion} · ${dimensionsPortion}`
  }

  if (weightPortion) {
    return weightPortion
  }

  if (dimensionsPortion) {
    return dimensionsPortion
  }

  return 'Weight and dimensions not recorded for this order'
}

const FulfillmentOrderDetails: React.FC<FulfillmentOrderDetailsProps> = ({ orderId }) => {
  const dispatch = useAppDispatch()
  const { loading, success, error } = useSelector((state: RootState) => state.getFulfillmentOrderDetailsForAdmin)
  const {
    loading: tripDetailsLoading,
    success: tripDetailsSuccess,
    error: tripDetailsError
  } = useSelector((state: RootState) => state.getFulfillmentOrderTripDetailsForAdmin)
  const isAdmin = getUserRole() === UserRole.admin

  useEffect(() => {
    if (orderId) {
      const payload: GetFulfillmentOrderDetailsForAdminRequestPayload = { request: { orderId } }
      dispatch(getFulfillmentOrderDetailsForAdmin(payload))

      if (isAdmin) {
        const tripDetailsPayload: GetFulfillmentOrderTripDetailsForAdminRequestPayload = { request: { orderId } }
        dispatch(getFulfillmentOrderTripDetailsForAdmin(tripDetailsPayload))
      }
    }
  }, [orderId, dispatch, isAdmin])

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" sx={styles.skeletonCard} height={400} />
        <Skeleton variant="rectangular" sx={styles.skeletonCard} height={200} />
      </Box>
    )
  }

  if (error || !success?.data) {
    return (
      <EmptyState
        title="Order not found"
        subtitle="Could not load fulfillment order details"
        errorId={error?.correlationId}
      />
    )
  }

  const order: AdminFulfillmentOrderDetails = success.data
  const visibleTrackingSteps = order.trackingSteps.filter((s) => s.visible)
  const activeStep = computeActiveStepIndex(order.trackingSteps, order.trackingDetails)
  const isLastStepComplete = activeStep === visibleTrackingSteps.length

  // ── Person card ──────────────────────────────────────────────────
  const renderPersonCard = (name: string, email: string, children?: React.ReactNode) => {
    const initials = getInitials(name)
    const avatarBg = getAvatarColor(name)

    return (
      <Box sx={styles.personCard}>
        <Avatar sx={styles.avatarSx(avatarBg)}>{initials}</Avatar>

        <Box sx={styles.flexContent}>
          <Typography variant="body2" fontWeight={600} sx={styles.personCardName}>
            {name}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={styles.personCardEmail(!!children)}>
            {email}
          </Typography>

          {children}
        </Box>
      </Box>
    )
  }

  // ── Order details (items) ─────────────────────────────────────────
  const renderItems = () => {
    const displayOrderId = stripPrefixFromId(order.orderId)
    const headerSubtitle = buildOrderPhysicalDetailsSubtitle(order)

    const title = (
      <Box sx={styles.orderDetailsHeaderRow}>
        <Box sx={styles.orderDetailsHeaderColumn}>
          <Typography variant="subtitle1" fontWeight={600}>
            {`Order Details · ${displayOrderId}`}
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={styles.orderDetailsHeaderSubtitleText}>
            {headerSubtitle}
          </Typography>
        </Box>

        <Chip
          label={order.status.displayName || order.status.name}
          color={getStatusColor(order.status.name)}
          size="small"
          sx={styles.orderDetailsStatusChip}
        />
      </Box>
    )

    const content = (
      <Box>
        {order.items.map((item) => (
          <Box key={item.itemId} sx={styles.itemCard}>
            {item.imageUrls.length > 0 ? (
              <Box component="img" src={item.imageUrls[0]} alt={item.description} sx={styles.itemImage} />
            ) : (
              <Box sx={styles.itemImagePlaceholder}>
                <Inventory2Outlined sx={styles.itemPlaceholderIcon} />
              </Box>
            )}

            <Box sx={styles.flexContent}>
              <Box sx={styles.itemHeaderRow}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {item.description}
                </Typography>

                <Box sx={styles.itemChipsRow}>
                  {item.fragile && <Chip label="Fragile" size="small" color="warning" sx={styles.badgeChip} />}
                  {item.repackagingAllowed && (
                    <Chip label="Repack OK" size="small" variant="outlined" sx={styles.badgeChip} />
                  )}
                </Box>
              </Box>

              <Box sx={styles.itemMeta}>
                <Box sx={styles.metaItem}>
                  <Typography sx={styles.label}>Qty</Typography>
                  <Typography variant="body2">{item.quantity}</Typography>
                </Box>

                {item.lengthCm && item.widthCm && item.heightCm && (
                  <Box sx={styles.metaItem}>
                    <Typography sx={styles.label}>Dimensions</Typography>

                    <Typography variant="body2" sx={styles.dimensionsText}>
                      {item.lengthCm}×{item.widthCm}×{item.heightCm} cm
                    </Typography>
                  </Box>
                )}

                <Box sx={styles.metaItem}>
                  <Typography sx={styles.label}>Value</Typography>

                  <Typography variant="body2" sx={styles.itemValueText}>
                    {formatMoney(item.declaredValueCents / 100, order.currency)}
                  </Typography>
                </Box>
              </Box>

              {item.specialInstructions && (
                <Box sx={styles.noteBox}>
                  <Typography sx={styles.labelWithMargin}>Special Instructions</Typography>

                  <Typography variant="body2" color="text.secondary" sx={styles.bodySmallText}>
                    {item.specialInstructions}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    )

    return (
      <Accordion
        header={{ mainContent: title, fullWidth: true }}
        body={content}
        isExpanded={true}
        preventExpandChange
        noExpandIcon
      />
    )
  }

  // ── Sender & Recipient ───────────────────────────────────────────
  const renderSenderRecipient = () => {
    const recipientDetails = (
      <Box sx={styles.recipientDetails}>
        {order.recipient.phone && (
          <Typography variant="body2" color="text.secondary" sx={styles.bodySmallText}>
            {order.recipient.phone}
          </Typography>
        )}

        <Box sx={styles.addressBox}>
          <Typography variant="body2" color="text.secondary" sx={styles.addressText}>
            {order.recipient.streetAddress}
            <br />
            {order.recipient.city}, {order.recipient.stateProvince} {order.recipient.postalCode}
            <br />
            {order.recipient.country}
          </Typography>
        </Box>

        {order.recipient.deliveryInstructions && (
          <Box sx={styles.noteBox}>
            <Typography sx={styles.labelWithMargin}>Delivery Instructions</Typography>

            <Typography variant="body2" color="text.secondary" sx={styles.bodySmallText}>
              {order.recipient.deliveryInstructions}
            </Typography>
          </Box>
        )}

        <Box sx={styles.verifiedRow}>
          {order.recipient.verified ? (
            <>
              <VerifiedOutlined sx={styles.verifiedIcon} />

              <Typography variant="caption" color="success.main" fontWeight={600}>
                Verified
              </Typography>

              {order.recipient.verifiedAt && (
                <Typography variant="caption" color="text.disabled">
                  · {date(order.recipient.verifiedAt).format(dateTimeFormats.date.medium)}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="caption" color="text.disabled">
              Unverified
            </Typography>
          )}
        </Box>
      </Box>
    )

    const content = (
      <Box sx={styles.contentContainer}>
        <Box>
          <Typography sx={styles.sectionLabel}>Sender</Typography>
          <>{renderPersonCard(`${order.sender.firstName} ${order.sender.lastName}`, order.sender.email)}</>
        </Box>

        <Box sx={styles.sectionContainer}>
          <Typography sx={styles.sectionLabel}>Recipient</Typography>
          {renderPersonCard(order.recipient.name, order.recipient.email, recipientDetails)}
        </Box>
      </Box>
    )

    const headerButtons = order.uspsLabelUrl
      ? [
          {
            buttonType: 'secondary' as const,
            text: 'View USPS Label',
            icon: <LocalShippingOutlined />,
            iconPosition: 'start' as const,
            onClick: () => window.open(order.uspsLabelUrl!, '_blank')
          }
        ]
      : undefined

    return (
      <Accordion
        header={{ mainContent: 'Sender & Recipient', buttons: headerButtons }}
        body={content}
        isExpanded={true}
      />
    )
  }

  // ── Traveler Details ─────────────────────────────────────────────
  const renderTravelerDetails = () => {
    const dropoff = order.uspsDropoffLocation

    const dropoffSection = dropoff && (
      <Box sx={styles.sectionContainer}>
        <Typography sx={styles.sectionLabel}>USPS Drop-off Location</Typography>

        <Box sx={styles.personCard}>
          <Box sx={styles.flexContent}>
            {dropoff.name && (
              <Typography variant="body2" fontWeight={600} sx={styles.personCardName}>
                {dropoff.name}
              </Typography>
            )}

            <Box sx={styles.addressBox}>
              <Typography variant="body2" color="text.secondary" sx={styles.addressText}>
                {dropoff.streetAddress}
                {(dropoff.city || dropoff.state || dropoff.zip) && (
                  <>
                    <br />
                    {[dropoff.city, dropoff.state].filter(Boolean).join(', ')}
                    {dropoff.zip ? ` ${dropoff.zip}` : ''}
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    )

    const content = order.traveler ? (
      <Box sx={styles.contentContainer}>
        {renderPersonCard(`${order.traveler.firstName} ${order.traveler.lastName}`, order.traveler.email)}
        {dropoffSection}
      </Box>
    ) : (
      <EmptyState title="No traveler assigned" subtitle="No traveler has accepted this order yet" />
    )

    return <Accordion header={{ mainContent: 'Traveler Details' }} body={content} isExpanded={false} />
  }

  // ── Trip Details ─────────────────────────────────────────────────
  const renderTripDetails = () => {
    if (tripDetailsLoading) {
      return <Skeleton variant="rectangular" sx={styles.skeletonCard} height={50} />
    }

    let content = null

    if (tripDetailsSuccess?.data) {
      content = (
        <Box sx={styles.contentContainer}>
          <TripDetailsCard tripDetails={tripDetailsSuccess.data} />
        </Box>
      )
    } else if (tripDetailsError) {
      content = (
        <EmptyState
          title="No trip details found"
          subtitle="No traveler has accepted this order yet"
          errorId={tripDetailsError.correlationId}
        />
      )
    }

    return <Accordion header={{ mainContent: 'Trip Details' }} body={content} isExpanded={false} />
  }

  // ── Tracking ─────────────────────────────────────────────────────
  const renderTracking = () => {
    const reversedEvents = [...order.tracking].reverse()

    const content = (
      <Box sx={styles.trackingDetailsContainer}>
        {visibleTrackingSteps.length > 0 && (
          <Stepper activeStep={activeStep} alternativeLabel sx={styles.stepper}>
            {visibleTrackingSteps.map((step) => (
              <Step key={step.key} completed={isLastStepComplete || activeStep > visibleTrackingSteps.indexOf(step)}>
                <StepLabel>
                  {step.label}
                  {step.subLabel && (
                    <Typography variant="caption" display="block">
                      {step.subLabel}
                    </Typography>
                  )}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {reversedEvents.length > 0 ? (
          <>
            <Box sx={styles.recentEventsDividerRow}>
              <Divider sx={styles.flexContent} />
              <Typography sx={styles.recentEventsDividerLabel}>Recent Events</Typography>
              <Divider sx={styles.flexContent} />
            </Box>

            <Box sx={styles.timelineContainer}>
              <Box sx={styles.timelineLine} />

              {reversedEvents.map((event, idx) => (
                <Box key={idx} sx={styles.timelineEvent}>
                  <Box sx={idx === 0 ? styles.timelineDotFirst : styles.timelineDot} />

                  <Typography variant="body2" sx={styles.eventDescriptionText}>
                    {event.eventDescription}
                  </Typography>

                  <Box sx={styles.eventHeaderRow}>
                    <Typography variant="caption" color="text.disabled">
                      {date(event.timestamp).format(dateTimeFormats.date.medium)}
                    </Typography>
                  </Box>

                  {event.location && (
                    <Box sx={styles.eventLocationRow}>
                      <FmdGoodOutlined sx={styles.locationIcon} />

                      <Typography variant="caption" color="text.disabled">
                        {event.location}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <EmptyState title="No tracking events" subtitle="No tracking events recorded yet" />
        )}
      </Box>
    )

    return <Accordion header={{ mainContent: 'Tracking' }} body={content} isExpanded={false} />
  }

  // ── Payment Details ──────────────────────────────────────────────
  const renderPaymentDetails = () => {
    const { feeBreakdown } = order

    if (!feeBreakdown) {
      return null
    }

    const feeRow = (label: string, cents: number, currency: string) => (
      <Box sx={styles.paymentRow}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" sx={styles.amount}>
          {formatMoney(cents / 100, currency)}
        </Typography>
      </Box>
    )

    const content = (
      <Box>
        {/* ── Sender ───────────────────────────────── */}
        <Typography sx={styles.sectionLabel}>Sender</Typography>
        <Box sx={styles.paymentCard}>
          {order.shopperPayment && (
            <Box sx={styles.paymentRow}>
              <Typography variant="body2" color="text.secondary">
                Shopper Payment
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={order.shopperPayment.status.displayName}
                  color={getStatusColor(order.shopperPayment.status.name)}
                  size="small"
                />
                <Typography variant="body2" sx={styles.amount}>
                  {formatMoney(order.shopperPayment.amountCents / 100, order.shopperPayment.currency)}
                </Typography>
              </Box>
            </Box>
          )}

          {feeBreakdown.hourrierFeeCents != null &&
            feeBreakdown.hourrierFeeCents > 0 &&
            feeRow('Hourrier Fee', feeBreakdown.hourrierFeeCents, feeBreakdown.currency)}

          {feeBreakdown.airportFeeCents != null &&
            feeBreakdown.airportFeeCents > 0 &&
            feeRow('Airport Fee', feeBreakdown.airportFeeCents, feeBreakdown.currency)}

          {feeBreakdown.distantFeeCents != null &&
            feeBreakdown.distantFeeCents > 0 &&
            feeRow('Distant Fee', feeBreakdown.distantFeeCents, feeBreakdown.currency)}

          {feeBreakdown.uspsFeeCents != null &&
            feeBreakdown.uspsFeeCents > 0 &&
            feeRow(
              `USPS Fee${feeBreakdown.uspsFeeIsEstimate ? ' (estimated)' : ' (final)'}`,
              feeBreakdown.uspsFeeCents,
              feeBreakdown.currency
            )}

          {feeBreakdown.stripeFeeCents != null &&
            feeBreakdown.stripeFeeCents > 0 &&
            feeRow('Stripe Fee', feeBreakdown.stripeFeeCents, feeBreakdown.currency)}

          {feeBreakdown.travelerBenefitCents != null &&
            feeBreakdown.travelerBenefitCents > 0 &&
            feeRow('Traveler Benefit', feeBreakdown.travelerBenefitCents, feeBreakdown.currency)}
        </Box>

        {/* ── Traveler ─────────────────────────────── */}
        <Typography sx={{ ...styles.sectionLabel, mt: 2 }}>Traveler</Typography>

        {!order.travelerCollateral &&
          !order.travelerTransfer &&
          !order.uspsPaymentStatus &&
          !order.uspsPaymentDeadline && (
            <EmptyState
              noIcon
              title="No payments on record"
              subtitle="Traveler payments appear once collateral is held"
              marginTop={1}
            />
          )}

        {(order.travelerCollateral ||
          order.travelerTransfer ||
          order.uspsPaymentStatus ||
          order.uspsPaymentDeadline) && (
          <Box sx={styles.paymentCard}>
            {order.travelerCollateral && (
              <Box sx={styles.paymentRow}>
                <Typography variant="body2" color="text.secondary">
                  Traveler Collateral
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={order.travelerCollateral.status.displayName}
                    color={getStatusColor(order.travelerCollateral.status.name)}
                    size="small"
                  />
                  <Typography variant="body2" sx={styles.amount}>
                    {formatMoney(order.travelerCollateral.amountCents / 100, order.travelerCollateral.currency)}
                  </Typography>
                </Box>
              </Box>
            )}

            {order.travelerTransfer &&
              (order.travelerTransfer.transfer ? (
                <Box sx={styles.paymentRow}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Transfer
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {date(order.travelerTransfer.transfer.completedAt).format(
                        `${dateTimeFormats.date.medium}, ${dateTimeFormats.time.short}`
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={order.travelerTransfer.transfer.status.displayName}
                      color={getStatusColor(order.travelerTransfer.transfer.status.name)}
                      size="small"
                    />
                    <Typography variant="body2" sx={styles.amount}>
                      {formatMoney(
                        order.travelerTransfer.transfer.amountCents / 100,
                        order.travelerTransfer.transfer.currency
                      )}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={styles.paymentRow}>
                  <Typography variant="body2" color="text.secondary">
                    Transfer Request
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={order.travelerTransfer.requestStatus.displayName}
                      color={getStatusColor(order.travelerTransfer.requestStatus.name)}
                      size="small"
                    />
                    <Typography variant="body2" sx={styles.amount}>
                      {formatMoney(order.travelerTransfer.requestedAmountCents / 100, order.travelerTransfer.currency)}
                    </Typography>
                  </Box>
                </Box>
              ))}

            {order.uspsPaymentStatus && (
              <Box sx={styles.paymentRow}>
                <Typography variant="body2" color="text.secondary">
                  USPS Payment
                </Typography>
                <Chip
                  label={order.uspsPaymentStatus.displayName}
                  color={getStatusColor(order.uspsPaymentStatus.name)}
                  size="small"
                />
              </Box>
            )}

            {order.uspsPaymentDeadline && (
              <Box sx={styles.paymentRow}>
                <Typography variant="body2" color="text.secondary">
                  USPS Payment Deadline
                </Typography>
                <Typography variant="body2" sx={styles.paymentDeadlineText}>
                  {date(order.uspsPaymentDeadline).format(dateTimeFormats.date.medium)}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    )

    return <Accordion header={{ mainContent: 'Payment Details' }} body={content} isExpanded={true} />
  }

  return (
    <Box sx={styles.container}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6.5}>
          {renderItems()}
        </Grid>

        <Grid item xs={12} md={5.5}>
          <Box sx={styles.sideSection}>{renderSenderRecipient()}</Box>
          <Box sx={styles.sideSection}>{renderTravelerDetails()}</Box>
          {isAdmin && <Box sx={styles.sideSection}>{renderTripDetails()}</Box>}
          <Box sx={styles.sideSection}>{renderTracking()}</Box>
          {isAdmin && <Box sx={styles.sideSection}>{renderPaymentDetails()}</Box>}
        </Grid>
      </Grid>
    </Box>
  )
}

export default FulfillmentOrderDetails
